import { LevelSelectionScene } from "./LevelSelectionScene";
import { SelectionItem } from "./SelectionItem";
import { MyPoint } from "../../../typescript/MyPoint";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { GameManager } from "../../GameManager";

export class LevelSelectionManager extends Phaser.GameObjects.Container {

    public levelSelectionScene: LevelSelectionScene;

    private levelItems: SelectionItem[];
    private verticalSpacing: number;
    private horizontalSpacing: number;
    private originPoint: MyPoint;

    private tweenActive: boolean;
    private scrollDuration: number = 300;
    private popupDuration: number = 250;

    constructor(scene: LevelSelectionScene) {

        super(scene);
        this.levelSelectionScene = scene;
        this.create();
    }

    public create(): void {

        GameVars.currentScene = this.levelSelectionScene;

        if (GameVars.aspectRatio <= 1.80) {
            this.verticalSpacing = GameConstants.GAME_WIDTH / 2.6;
            this.horizontalSpacing = GameConstants.GAME_WIDTH / 3;
        } else {
            this.verticalSpacing = GameConstants.GAME_WIDTH / 3.3;
            this.horizontalSpacing = GameConstants.GAME_WIDTH / 2.3;
        }

        if (GameVars.aspectRatio < 1.80) {
            this.originPoint = new MyPoint(GameConstants.GAME_WIDTH / 6, (GameConstants.GAME_HEIGHT / 2) - (GameConstants.GAME_HEIGHT / 4.50));
        } else {
            this.originPoint = new MyPoint((GameConstants.GAME_WIDTH * GameVars.scaleXMultInverse) / 2 - (GameConstants.GAME_WIDTH * GameVars.scaleXMultInverse) / 4.50, ((GameConstants.GAME_HEIGHT) / 2) - ((GameConstants.GAME_HEIGHT) / 3.5));
        }

        this.levelItems = [];
        this.tweenActive = false;

        this.createCategoryPages(false);
    }

    public createCategoryPages(doScroll: boolean, toLeft?: boolean, popupFromBelow?: boolean): void {

        this.cleanCategoryPages(doScroll, toLeft, popupFromBelow);

        GameVars.currentDrawCategory = GameManager.getCategoryFromID(GameVars.currentDrawCategoryID);
        let drawsInCategory = this.getDrawsAmmountInCategory(GameVars.currentDrawCategory);

        let i = GameVars.currentCategoryPage * GameConstants.ITEMS_PER_PAGE;
        let limit = i + GameConstants.ITEMS_PER_PAGE;

        let counter = 0;
        let placer: MyPoint = new MyPoint(this.originPoint.x, this.originPoint.y);

        for (i; i < limit; i++) {

            if (i + 1 > drawsInCategory) {
                return;
            }

            let selectionItem = new SelectionItem(this.levelSelectionScene, i, GameVars.currentDrawCategory);

            if (GameVars.aspectRatio <= 1.80) {
                if (counter < GameConstants.ITEMS_PER_PAGE / 2) {
                    selectionItem.setPosition(this.originPoint.x + (counter++ * this.horizontalSpacing), this.originPoint.y);
                } else {
                    selectionItem.setPosition(this.originPoint.x + ((counter++ - GameConstants.ITEMS_PER_PAGE / 2) * this.horizontalSpacing), this.originPoint.y + this.verticalSpacing);
                }
            } else {
                if (counter % 2 !== 0) {

                    selectionItem.setPosition(placer.x, placer.y);

                    placer.x = this.originPoint.x;
                    placer.y += this.verticalSpacing;

                } else {

                    selectionItem.setPosition(placer.x, placer.y);

                    placer.x += this.horizontalSpacing;
                }
                counter++;
            }

            if (GameVars.currentDrawCategoryID === GameVars.recentDrawFinishedCategoryID && i === GameVars.recentDrawFinishedID) {
                selectionItem.setScale(0.7);
            }

            if (doScroll || popupFromBelow) {

                this.tweenActive = true;

                if (doScroll) {

                    selectionItem.x -= (toLeft ? GameConstants.GAME_WIDTH : -GameConstants.GAME_WIDTH);

                    const tween = this.scene.tweens.create({
                        targets: [selectionItem],
                        ease: Phaser.Math.Easing.Cubic.Out,
                        x: selectionItem.x + (toLeft ? GameConstants.GAME_WIDTH : -GameConstants.GAME_WIDTH),
                        duration: this.scrollDuration
                    });
                    tween.play(true);
                    tween.setCallback("onComplete", function (): void {
                        this.tweenActive = false;
                    }, [null], this);

                } else {

                    const targetScaleX = selectionItem.scaleX;
                    const targetScaleY = selectionItem.scaleY;
                    selectionItem.scaleY *= .9;
                    selectionItem.scaleX *= .9;
                    selectionItem.alpha = 0;

                    const tween = this.scene.tweens.create({
                        targets: [selectionItem],
                        ease: Phaser.Math.Easing.Cubic.Out,
                        scaleY: targetScaleY,
                        scaleX: targetScaleX,
                        alpha: 1,
                        duration: this.popupDuration,
                        delay: this.popupDuration
                    });
                    tween.play(true);
                    tween.setCallback("onComplete", function (): void {
                        this.tweenActive = false;
                    }, [null], this);

                }
            }

            this.levelItems.push(selectionItem);
            this.add(selectionItem);
        }
    }

    public goNextPage(): void {

        if (this.tweenActive) {
            return;
        }

        let totalPages = this.getTotalPagesInCategory() + 1;

        GameVars.currentCategoryPage = (GameVars.currentCategoryPage + 1) % totalPages;

        this.levelSelectionScene.levelSelectionGUI.dottySlider.goToPage(GameVars.currentCategoryPage);
        this.createCategoryPages(true, false);
    }

    public goPrevPage(): void {

        if (this.tweenActive) {
            return;
        }

        let totalPages = this.getTotalPagesInCategory() + 1;

        let newPage = GameVars.currentCategoryPage - 1;
        if (newPage < 0) {
            newPage += totalPages;
        }
        GameVars.currentCategoryPage = newPage;

        this.levelSelectionScene.levelSelectionGUI.dottySlider.goToPage(GameVars.currentCategoryPage);

        this.createCategoryPages(true, true);
    }

    public goNextCategory(): void {

        if (this.tweenActive) {
            return;
        }

        GameVars.currentDrawCategoryID = (GameVars.currentDrawCategoryID + 1) % GameConstants.CATEGORIES_AMMOUNT;
        GameVars.currentDrawCategory = GameManager.getCategoryFromID(GameVars.currentDrawCategoryID);
        GameVars.currentCategoryPage = 0;
        this.levelSelectionScene.levelSelectionGUI.setCategoryText(GameVars.currentDrawCategory);
        this.createCategoryPages(false, false, true);

        GameVars.currentCategoryPage = 0;
    }

    public goPrevCategory(): void {

        if (this.tweenActive) {
            return;
        }
        let newCat = GameVars.currentDrawCategoryID - 1;
        if (newCat < 0) {
            newCat += GameConstants.CATEGORIES_AMMOUNT;
        }

        GameVars.currentDrawCategoryID = newCat;
        GameVars.currentDrawCategory = GameManager.getCategoryFromID(GameVars.currentDrawCategoryID);
        GameVars.currentCategoryPage = 0;
        this.levelSelectionScene.levelSelectionGUI.setCategoryText(GameVars.currentDrawCategory);
        this.createCategoryPages(false, false, true);

        GameVars.currentCategoryPage = 0;
    }

    public getTotalPagesInCategory(): number {

        return Phaser.Math.FloorTo(this.getPagesAmmountInCategory(GameVars.currentDrawCategory) - 1);
    }

    private cleanCategoryPages(doScroll: boolean, toLeft: boolean, popupFromBelow: boolean): void {

        this.levelItems.forEach(item => {

            if (doScroll) {

                const tween = this.scene.tweens.create({
                    targets: [item],
                    ease: Phaser.Math.Easing.Cubic.Out,
                    x: item.x + (toLeft ? GameConstants.GAME_WIDTH : -GameConstants.GAME_WIDTH),
                    duration: this.scrollDuration
                });
                tween.play(true);
                tween.setCallback("onComplete", function (): void {
                    item.visible = false;
                }, [null], this);

            } else if (popupFromBelow) {

                const tween = this.scene.tweens.create({
                    targets: [item],
                    ease: Phaser.Math.Easing.Cubic.Out,
                    scaleX: item.scaleX * .9,
                    scaleY: item.scaleY * .9,
                    alpha: 0,
                    duration: this.popupDuration
                });
                tween.play(true);
                tween.setCallback("onComplete", function (): void {
                    item.visible = false;
                }, [null], this);

            } else {
                item.visible = false;
            }
        });

        this.levelItems = [];
    }

    private getDrawsAmmountInCategory(categoryName: string): number {

        switch (categoryName) {

            case "ANIMALS":
                return GameVars.animalAmount;

            case "FOOD":
                return GameVars.foodAmount;

            case "HUMANS":
                return GameVars.peopleAmount;

            case "STUFF":
                return GameVars.stuffAmount;

            case "MONSTERS":
                return GameVars.monstersAmount;

            case "PLANTS":
                return GameVars.plantsAmount;

            case "VEHICLES":
                return GameVars.vehiclesAmount;

            default:
                break;
        }
    }

    private getPagesAmmountInCategory(categoryName: string): number {

        let catAmmount: number = 0;

        switch (categoryName) {

            case "ANIMALS":
                catAmmount = GameVars.animalAmount;
                break;

            case "FOOD":
                catAmmount = GameVars.foodAmount;
                break;

            case "HUMANS":
                catAmmount = GameVars.peopleAmount;
                break;

            case "STUFF":
                catAmmount = GameVars.stuffAmount;
                break;

            case "MONSTERS":
                catAmmount = GameVars.monstersAmount;
                break;

            case "PLANTS":
                catAmmount = GameVars.plantsAmount;
                break;

            case "VEHICLES":
                catAmmount = GameVars.vehiclesAmount;
                break;

            default:
                break;
        }

        let ammount = catAmmount / GameConstants.ITEMS_PER_PAGE;

        if (ammount % 1 !== 0) {
            ammount++;
        }

        return Phaser.Math.FloorTo(ammount);
    }
}
